import React, { useCallback, useEffect, useState } from "react";
import { IoCloudOfflineOutline } from "react-icons/io5";
import { UserService } from "../../services/UserService";

const STATUS_URL = "https://status.hashh.io/";

// No answer at all, or the server (or the proxy in front of it) failing. A
// refusal such as 401 still means the server is up.
const isDown = (error) => !error?.status || error.status >= 500;

// The app's network alert on Home (FirstScreen.js getHealth and
// components/AlertMessage/NetworkAlert.js): when the browser is offline, or the
// server doesn't answer GET user/hello, a banner says so and links to the
// status page. The app's offline mode talks to devices on the local network,
// which a browser can't, so it isn't offered here.
function NetworkAlert() {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  const [serverDown, setServerDown] = useState(false);
  const [checking, setChecking] = useState(false);

  const check = useCallback(() => {
    setChecking(true);
    return UserService.getHealth()
      .then(() => setServerDown(false))
      .catch((error) => setServerDown(isDown(error)))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    let current = true;
    UserService.getHealth()
      .then(() => current && setServerDown(false))
      .catch((error) => current && setServerDown(isDown(error)));
    const goOnline = () => {
      setOnline(true);
      check();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      current = false;
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [check]);

  if (online && !serverDown) return null;

  return (
    <div className="hx-net-alert" role="alert">
      <IoCloudOfflineOutline className="hx-net-alert-icon" aria-hidden="true" />
      <p className="hx-net-alert-text">
        {online
          ? "Oops! It looks like we're having trouble reaching our server right now."
          : "Your internet connection is currently unavailable."}
      </p>
      <div className="hx-net-alert-buttons">
        {online ? (
          <a className="hx-net-alert-link" href={STATUS_URL} target="_blank" rel="noopener noreferrer">
            Check status
          </a>
        ) : null}
        <button type="button" className="hx-btn" onClick={check} disabled={checking || !online}>
          {checking ? "Checking…" : "Try again"}
        </button>
      </div>
    </div>
  );
}

export default NetworkAlert;
